import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { expensesAPI, expenseAttachmentsAPI, expenseLimitsAPI } from '../../services/api';

export const EXPENSE_CATEGORIES = [
  'travel',
  'meals',
  'mileage',
  'supplies',
  'software',
  'lodging',
  'clientExpense',
  'other',
] as const;

export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

export interface Expense {
  id: number;
  amount: number;
  currency: string;
  category: ExpenseCategory;
  merchant?: string;
  expense_date: string;
  project_id?: number | null;
  description?: string;
  billable: boolean;
  reimbursable: boolean;
  status: string;
  expense_attachments?: Array<{
    id: number;
    file_url: string;
    file_name: string;
    content_type: string;
    file_size: number;
    human_file_size: string;
    is_image: boolean;
    created_at: string;
  }>;
}

interface Project {
  id: number;
  name: string;
}

interface ExpenseFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  expenseToEdit?: Expense | null;
  projects?: Project[];
}

const CURRENCIES = ['CAD', 'USD', 'EUR', 'GBP'];

const ExpenseForm: React.FC<ExpenseFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  expenseToEdit,
  projects,
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { showToast } = useToast();
  const token = localStorage.getItem('access_token') || '';

  const [formData, setFormData] = useState({
    amount: '',
    currency: 'CAD',
    category: '' as ExpenseCategory | '',
    merchant: '',
    expense_date: '',
    project_id: '' as string | number,
    description: '',
    billable: false,
    reimbursable: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<
    Array<{
      id: number;
      file_url: string;
      file_name: string;
      content_type: string;
      file_size: number;
      human_file_size: string;
      is_image: boolean;
      created_at: string;
    }>
  >([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<number[]>(
    [],
  );

  // Populate form when expenseToEdit changes
  React.useEffect(() => {
    if (expenseToEdit) {
      setFormData({
        amount: String(expenseToEdit.amount),
        currency: expenseToEdit.currency || 'CAD',
        category: expenseToEdit.category,
        merchant: expenseToEdit.merchant || '',
        expense_date: expenseToEdit.expense_date,
        project_id: expenseToEdit.project_id ?? '',
        description: expenseToEdit.description || '',
        billable: expenseToEdit.billable,
        reimbursable: expenseToEdit.reimbursable,
      });
      setExistingAttachments(expenseToEdit.expense_attachments || []);
      setRemovedAttachmentIds([]);
    } else {
      setFormData({
        amount: '',
        currency: 'CAD',
        category: '',
        merchant: '',
        expense_date: '',
        project_id: '',
        description: '',
        billable: false,
        reimbursable: true,
      });
      setExistingAttachments([]);
      setRemovedAttachmentIds([]);
    }
    setErrors({});
    setPendingFiles([]);
  }, [expenseToEdit, isOpen]);

  const removeExistingAttachment = (attachmentId: number) => {
    setRemovedAttachmentIds([...removedAttachmentIds, attachmentId]);
    setExistingAttachments(
      existingAttachments.filter((a) => a.id !== attachmentId),
    );
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    const amountNum = parseFloat(formData.amount);
    if (!formData.amount || isNaN(amountNum) || amountNum <= 0) {
      newErrors.amount =
        t('expense.errors.amountRequired') || 'Amount must be greater than 0';
    }

    if (!formData.category) {
      newErrors.category =
        t('expense.errors.categoryRequired') || 'Category is required';
    }

    if (!formData.expense_date) {
      newErrors.expense_date =
        t('expense.errors.dateRequired') || 'Date is required';
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selected = new Date(formData.expense_date + 'T00:00:00');
      if (selected > today) {
        newErrors.expense_date =
          t('expense.errors.dateNotFuture') || 'Date cannot be in the future';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = (status: 'draft' | 'pending') => ({
    amount: parseFloat(formData.amount),
    currency: formData.currency,
    category: formData.category,
    merchant_name: formData.merchant || undefined,
    expense_date: formData.expense_date,
    project_id: formData.project_id !== '' ? Number(formData.project_id) : null,
    description: formData.description || undefined,
    billable: formData.billable,
    reimbursable: formData.reimbursable,
    status,
  });

  const handleSaveDraft = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      let expenseId: number;
      if (expenseToEdit) {
        await expensesAPI.update(expenseToEdit.id, buildPayload('draft'));
        expenseId = expenseToEdit.id;
      } else {
        const response = await expensesAPI.create(buildPayload('draft'));
        expenseId = response.data?.expense?.id ?? response.data?.id;
      }

      // Remove deleted attachments only when we can resolve the expense id.
      if (removedAttachmentIds.length > 0 && expenseId) {
        for (const attachmentId of removedAttachmentIds) {
          await expenseAttachmentsAPI.delete(expenseId, attachmentId);
        }
      }

      // Upload new files if any
      if (pendingFiles.length > 0) {
        for (const file of pendingFiles) {
          const uploadResponse = await expenseAttachmentsAPI.upload(
            expenseId,
            file,
          );
          const attachment =
            uploadResponse.data?.attachment || uploadResponse.data;
          if (attachment && attachment.id) {
            setExistingAttachments((prev) => [...prev, attachment]);
          }
        }
      }

      showToast(
        t('expense.messages.updateSuccess') || 'Expense saved as draft',
        'success',
      );
      setPendingFiles([]);
      setRemovedAttachmentIds([]);
      onSuccess();
      onClose();
    } catch {
      showToast(
        t('expense.messages.updateFailed') || 'Failed to save expense',
        'error',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    // Check expense limits
    try {
      const limitsResponse = await expenseLimitsAPI.getAll();
      const limits = limitsResponse.data.expense_limits || [];
      const categoryLimits = limits.filter((l: any) => l.category === formData.category && l.active);

      if (categoryLimits.length > 0) {
        const amount = parseFloat(formData.amount);
        const expenseDate = new Date(formData.expense_date);
        const currentYear = expenseDate.getFullYear();
        const currentMonth = expenseDate.getMonth();

        // Get user's expenses for this category
        const expensesResponse = await expensesAPI.getAll({
          category: formData.category,
          status: 'pending,approved'
        });
        const userExpenses = expensesResponse.data.expenses || [];

        for (const limit of categoryLimits) {
          let totalSpent = userExpenses.reduce((sum: number, exp: any) => {
            const expDate = new Date(exp.expense_date);
            const isSameYear = expDate.getFullYear() === currentYear;
            const isSameMonth = expDate.getMonth() === currentMonth;

            if (limit.period === 'monthly' && isSameYear && isSameMonth) {
              return sum + exp.amount;
            }
            if (limit.period === 'yearly' && isSameYear) {
              return sum + exp.amount;
            }
            return sum;
          }, 0);

          if (totalSpent + amount > limit.limit_amount) {
            const periodLabel = limit.period === 'monthly' ? 'monthly' : 'yearly';
            setErrors({
              amount: `Exceeds ${periodLabel} limit of ${limit.limit_amount} for this category`
            });
            setSubmitting(false);
            return;
          }
        }
      }
    } catch (err) {
      console.error('Failed to check expense limits:', err);
      // Continue with submission even if limit check fails
    }

    setSubmitting(true);
    try {
      let expenseId: number;
      if (expenseToEdit) {
        await expensesAPI.update(expenseToEdit.id, buildPayload('pending'));
        expenseId = expenseToEdit.id;
        showToast(
          t('expense.messages.updateSuccess') || 'Expense updated',
          'success',
        );
      } else {
        const response = await expensesAPI.create(buildPayload('pending'));
        expenseId = response.data?.expense?.id ?? response.data?.id;
        showToast(
          t('expense.messages.submitSuccess') ||
            'Expense submitted for approval',
          'success',
        );
      }

      // Remove deleted attachments only when we can resolve the expense id.
      if (removedAttachmentIds.length > 0 && expenseId) {
        for (const attachmentId of removedAttachmentIds) {
          await expenseAttachmentsAPI.delete(expenseId, attachmentId);
        }
      }

      // Upload new files if any and we can resolve the expense id.
      if (pendingFiles.length > 0 && expenseId) {
        for (const file of pendingFiles) {
          const uploadResponse = await expenseAttachmentsAPI.upload(
            expenseId,
            file,
          );
          const attachment =
            uploadResponse.data?.attachment || uploadResponse.data;
          if (attachment && attachment.id) {
            setExistingAttachments((prev) => [...prev, attachment]);
          }
        }
      }

      setPendingFiles([]);
      setRemovedAttachmentIds([]);
      onSuccess();
      onClose();
    } catch {
      showToast(
        expenseToEdit
          ? t('expense.messages.updateFailed') || 'Failed to update expense'
          : t('expense.messages.submitFailed') || 'Failed to submit expense',
        'error',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const inputClass = `w-full px-3 py-2 border rounded-lg ${
    isDark
      ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
      : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500'
  }`;

  const labelClass = `block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`;

  const errorClass = `text-xs mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
      <div
        className={`${
          isDark ? 'bg-slate-800' : 'bg-white'
        } rounded-lg p-6 max-w-2xl w-full mx-4 my-8`}
      >
        <h2
          className={`text-xl font-bold mb-5 ${isDark ? 'text-white' : 'text-slate-900'}`}
        >
          {expenseToEdit
            ? t('expense.editExpense') || 'Edit Expense'
            : t('expense.addExpense') || 'Add Expense'}
        </h2>

        <form onSubmit={handleSubmit} noValidate>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Amount */}
            <div>
              <label className={labelClass}>
                {t('expense.amount') || 'Amount'} *
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={formData.amount}
                onChange={(e) =>
                  setFormData({ ...formData, amount: e.target.value })
                }
                placeholder="0.00"
                className={inputClass}
              />
              {errors.amount && <p className={errorClass}>{errors.amount}</p>}
            </div>

            {/* Currency */}
            <div>
              <label className={labelClass}>
                {t('expense.currency') || 'Currency'}
              </label>
              <select
                value={formData.currency}
                onChange={(e) =>
                  setFormData({ ...formData, currency: e.target.value })
                }
                className={inputClass}
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Category */}
            <div>
              <label className={labelClass}>
                {t('expense.category') || 'Category'} *
              </label>
              <select
                value={formData.category}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    category: e.target.value as ExpenseCategory | '',
                  })
                }
                className={inputClass}
              >
                <option value="">
                  -- {t('expense.category') || 'Select category'} --
                </option>
                {EXPENSE_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {t(`expense.categories.${cat}`) || cat}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className={errorClass}>{errors.category}</p>
              )}
            </div>

            {/* Merchant */}
            <div>
              <label className={labelClass}>
                {t('expense.merchant') || 'Merchant/Vendor'}
              </label>
              <input
                type="text"
                value={formData.merchant}
                onChange={(e) =>
                  setFormData({ ...formData, merchant: e.target.value })
                }
                placeholder={t('expense.merchant') || 'Merchant/Vendor'}
                className={inputClass}
              />
            </div>

            {/* Expense Date */}
            <div>
              <label className={labelClass}>
                {t('expense.date') || 'Expense Date'} *
              </label>
              <input
                type="date"
                value={formData.expense_date}
                onChange={(e) =>
                  setFormData({ ...formData, expense_date: e.target.value })
                }
                max={new Date().toISOString().split('T')[0]}
                className={inputClass}
              />
              {errors.expense_date && (
                <p className={errorClass}>{errors.expense_date}</p>
              )}
            </div>

            {/* Project */}
            <div>
              <label className={labelClass}>
                {t('expense.project') || 'Project'}
              </label>
              <select
                value={formData.project_id}
                onChange={(e) =>
                  setFormData({ ...formData, project_id: e.target.value })
                }
                className={inputClass}
              >
                <option value="">
                  -- {t('expense.project') || 'No project'} --
                </option>
                {projects?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Description - full width */}
            <div className="md:col-span-2">
              <label className={labelClass}>
                {t('expense.description') || 'Description'}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder={t('expense.description') || 'Optional description'}
                rows={3}
                className={inputClass}
              />
            </div>

            {/* File Upload - full width */}
            <div className="md:col-span-2">
              <label className={labelClass}>
                {t('expense.uploadReceipt') || 'Upload Receipt'}
              </label>
              <p
                className={`text-xs mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('expense.uploadReceiptHint') ||
                  'Images and PDFs up to 10 MB'}
              </p>
              <input
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setPendingFiles([...pendingFiles, ...files]);
                  e.target.value = '';
                }}
                className={`${inputClass} cursor-pointer`}
              />
              {/* Existing Attachments */}
              {existingAttachments.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-300 dark:border-slate-600">
                  <p
                    className={`text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                  >
                    {t('expense.existingFiles') || 'Attached Files'} (
                    {existingAttachments.length})
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {existingAttachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className={`relative group rounded-lg overflow-hidden border ${
                          isDark
                            ? 'bg-slate-700 border-slate-600'
                            : 'bg-slate-100 border-slate-200'
                        }`}
                      >
                        {/* Preview or Icon */}
                        <div
                          className={`aspect-square flex items-center justify-center ${isDark ? 'bg-slate-800' : 'bg-slate-50'}`}
                        >
                          {attachment.is_image ? (
                            <img
                              src={`${process.env.REACT_APP_API_URL}${attachment.file_url}?token=${token}`}
                              alt={attachment.file_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="text-3xl">📄</div>
                          )}
                        </div>

                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-1">
                          <p className="text-white text-xs text-center truncate mb-2 w-full px-1">
                            <img
                              src={`${process.env.REACT_APP_API_URL}${attachment.file_url}?token=${token}`}
                              alt={attachment.file_name}
                              className="w-6 h-6 object-cover mb-1"
                            />
                          </p>
                          <p
                            className={`text-xs mb-2 ${isDark ? 'text-slate-300' : 'text-slate-200'}`}
                          >
                            {attachment.human_file_size}
                          </p>
                          <button
                            type="button"
                            onClick={() =>
                              removeExistingAttachment(attachment.id)
                            }
                            className="text-xs px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New Pending Files */}
              {pendingFiles.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-300 dark:border-slate-600">
                  <p
                    className={`text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                  >
                    {t('expense.newFiles') || 'New Files'} (
                    {pendingFiles.length})
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {pendingFiles.map((file, idx) => (
                      <div
                        key={idx}
                        className={`relative group rounded-lg overflow-hidden border ${
                          isDark
                            ? 'bg-slate-700 border-slate-600'
                            : 'bg-slate-100 border-slate-200'
                        }`}
                      >
                        {/* Preview or Icon */}
                        <div
                          className={`aspect-square flex items-center justify-center ${isDark ? 'bg-slate-800' : 'bg-slate-50'}`}
                        >
                          {file.type.startsWith('image/') ? (
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="text-3xl">📄</div>
                          )}
                        </div>

                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-1">
                          <p className="text-white text-xs text-center truncate mb-2 w-full px-1">
                            {file.name}
                          </p>
                          <p
                            className={`text-xs mb-2 ${isDark ? 'text-slate-300' : 'text-slate-200'}`}
                          >
                            {(file.size / 1024 / 1024).toFixed(1)} MB
                          </p>
                          <button
                            type="button"
                            onClick={() =>
                              setPendingFiles(
                                pendingFiles.filter((_, i) => i !== idx),
                              )
                            }
                            className="text-xs px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Checkboxes - full width */}
            <div className="md:col-span-2 flex flex-wrap gap-6">
              {/* Billable */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.billable}
                  onChange={(e) =>
                    setFormData({ ...formData, billable: e.target.checked })
                  }
                  className="w-4 h-4 rounded accent-indigo-600"
                />
                <span
                  className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                >
                  {t('expense.billable') || 'Billable'}
                </span>
              </label>

              {/* Reimbursable */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.reimbursable}
                  onChange={(e) =>
                    setFormData({ ...formData, reimbursable: e.target.checked })
                  }
                  className="w-4 h-4 rounded accent-indigo-600"
                />
                <span
                  className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                >
                  {t('expense.reimbursable') || 'Reimbursable'}
                </span>
              </label>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                isDark
                  ? 'bg-slate-700 text-white hover:bg-slate-600'
                  : 'bg-slate-200 text-slate-900 hover:bg-slate-300'
              } disabled:opacity-50`}
            >
              {t('common.cancel') || 'Cancel'}
            </button>
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={submitting}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all border ${
                isDark
                  ? 'border-slate-500 text-slate-300 hover:bg-slate-700'
                  : 'border-slate-300 text-slate-700 hover:bg-slate-100'
              } disabled:opacity-50`}
            >
              {t('expense.saveDraft') || 'Save Draft'}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 transition-all"
            >
              {submitting
                ? t('expense.submitting') || 'Submitting...'
                : expenseToEdit
                  ? t('expense.submitExpense') || 'Update Expense'
                  : t('expense.submitExpense') || 'Submit Expense'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseForm;
